import { PAYOUT_BANKS } from '@inflip/shared';
import { Children, isValidElement, useEffect, useState, type ReactNode } from 'react';

import { Select } from './select';
import { useToasts } from '../lib/toast';

/**
 * 어드민 공용 UI 프리미티브 — Toss 스타일 (화이트 + 그레이 + 블루 포인트).
 * 모든 페이지는 색을 직접 쓰지 말고 이 컴포넌트/토큰만 조합한다.
 */

// ── 페이지 골격 ──────────────────────────────────────────────

export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
}) {
  return (
    <header className="mb-6 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">{title}</h1>
        {description && <p className="mt-1 text-sm text-muted-foreground">{description}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </header>
  );
}

export function Card({ className = '', children }: { className?: string; children: ReactNode }) {
  return (
    <div className={`rounded-lg border border-border bg-card p-5 shadow-soft ${className}`}>
      {children}
    </div>
  );
}

export function ErrorBanner({ message }: { message: string }) {
  return <div className="mb-4 rounded-md bg-red-50 px-4 py-3 text-sm text-red-600">{message}</div>;
}

export function EmptyState({ message }: { message: string }) {
  return (
    <div className="rounded-lg border border-dashed border-border p-12 text-center text-sm text-muted-foreground">
      {message}
    </div>
  );
}

export function LoadingRow({ label = '불러오는 중…' }: { label?: string }) {
  return <div className="py-2 text-sm text-muted-foreground">{label}</div>;
}

// ── 토스 스타일 조각 ─────────────────────────────────────────

/** 스탯 타일 — 라벨 + 큰 숫자. 대시보드/상세 요약용. tone=danger 는 값>0 경고 */
export function StatTile({
  label,
  value,
  tone = 'default',
}: {
  label: string;
  value: string | number;
  tone?: 'default' | 'danger';
}) {
  return (
    <div className={`rounded-lg px-4 py-3.5 ${tone === 'danger' ? 'bg-rose-50' : 'bg-muted/60'}`}>
      <p
        className={`text-xs font-semibold ${tone === 'danger' ? 'text-rose-600' : 'text-muted-foreground'}`}
      >
        {label}
      </p>
      <p
        className={`mt-1 truncate text-lg font-extrabold tabular-nums tracking-tight ${tone === 'danger' ? 'text-rose-600' : ''}`}
      >
        {value}
      </p>
    </div>
  );
}

/** 섹션 — 볼드 타이틀 + 콘텐츠. divided 면 자식 행 사이에 옅은 구분선 */
export function Section({
  title,
  action,
  divided = false,
  children,
}: {
  title: string;
  action?: ReactNode;
  divided?: boolean;
  children: ReactNode;
}) {
  return (
    <section className="mt-8">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-base font-extrabold tracking-tight">{title}</h2>
        {action}
      </div>
      <div className={`mt-2 ${divided ? 'divide-y divide-border/60' : ''}`}>{children}</div>
    </section>
  );
}

/** 라벨-값 한 줄 — 토스 설정 리스트 스타일 (라벨 왼쪽, 값 오른쪽). Section divided 와 함께 */
export function InfoRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 py-3 text-sm">
      <span className="shrink-0 text-muted-foreground">{label}</span>
      <span className="min-w-0 text-right font-medium">{children}</span>
    </div>
  );
}

// ── 다이얼로그 (토스 스타일 딤) — window.prompt/confirm 대체 ──

let dialogLockCount = 0;

/**
 * 창이 떠 있는 동안 뒤 페이지(#admin-content) 스크롤을 잠근다 — 창 밖에서 굴려도 뒤 목록이 움직이지 않게.
 * 창이 겹칠 수 있어 마지막 창이 닫힐 때만 푼다
 */
function useLockContentScroll() {
  useEffect(() => {
    const content = document.getElementById('admin-content');
    if (!content) return;
    dialogLockCount += 1;
    if (dialogLockCount === 1) content.style.overflowY = 'hidden';
    return () => {
      dialogLockCount -= 1;
      if (dialogLockCount === 0) content.style.overflowY = '';
    };
  }, []);
}

/** showToast 로 띄운 알림 — 화면 아래 가운데, 창(z-50) 위에 */
export function Toaster() {
  const toasts = useToasts();
  if (toasts.length === 0) return null;
  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-6 z-[60] flex flex-col items-center gap-2 px-4">
      {toasts.map((t) => (
        <p
          key={t.id}
          role="status"
          className="max-w-md rounded-md bg-foreground px-4 py-3 text-sm font-semibold text-background shadow-lg"
        >
          {t.message}
        </p>
      ))}
    </div>
  );
}

/** 창 아래 버튼 줄 — Dialog 의 자식으로 두면 내용이 길어도 스크롤되지 않고 아래에 고정된다 */
export function DialogFooter({
  notice,
  children,
}: {
  /** 버튼 바로 위에 고정해 보여 줄 알림 (서버 오류 등) — 내용을 스크롤해도 가려지지 않게 */
  notice?: ReactNode;
  children: ReactNode;
}) {
  return (
    <>
      {notice}
      <div className={`flex gap-2 ${notice ? 'mt-3' : ''}`}>{children}</div>
    </>
  );
}

export function Dialog({
  label,
  title,
  onClose,
  wide = false,
  children,
}: {
  label: string;
  /** 창 제목 — 위에 고정되고, 그 아래 내용만 창 안에서 스크롤된다 */
  title?: ReactNode;
  onClose: () => void;
  /** 입력이 많은 창 (계약 해지 등) — 넓게 */
  wide?: boolean;
  children: ReactNode;
}) {
  useLockContentScroll();
  const items = Children.toArray(children);
  const footer = items.filter((c) => isValidElement(c) && c.type === DialogFooter);
  const body = items.filter((c) => !(isValidElement(c) && c.type === DialogFooter));
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-5"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={label}
        className={`flex max-h-full w-full flex-col rounded-xl bg-card shadow-2xl ${wide ? 'max-w-lg' : 'max-w-sm'}`}
        onClick={(e) => e.stopPropagation()}
      >
        {title && (
          <div className="shrink-0 px-6 pt-6">
            <p className="text-lg font-bold tracking-tight">{title}</p>
          </div>
        )}
        {/* 화면보다 길면 제목 아래·버튼 위 내용만 스크롤 — 끝에 닿아도 뒤 페이지로 넘어가지 않게 */}
        <div
          className={`min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain px-6 ${title ? 'pt-1' : 'pt-6'} ${footer.length ? 'pb-1' : 'pb-6'}`}
        >
          {body}
        </div>
        {footer.length > 0 && <div className="shrink-0 px-6 pt-5 pb-6">{footer}</div>}
      </div>
    </div>
  );
}

/** 확인 다이얼로그 — "정말 할까요?" 류. danger 면 확인 버튼이 붉은 톤 */
export function ConfirmDialog({
  title,
  description,
  confirmLabel = '확인',
  danger = false,
  busy = false,
  onConfirm,
  onClose,
}: {
  title: string;
  description?: ReactNode;
  confirmLabel?: string;
  danger?: boolean;
  busy?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <Dialog label={title} title={title} onClose={onClose}>
      {description && <p className="text-sm text-muted-foreground">{description}</p>}
      <DialogFooter>
        <Button variant="secondary" className="flex-1" onClick={onClose}>
          취소
        </Button>
        <Button
          variant={danger ? 'danger' : 'primary'}
          className="flex-1"
          disabled={busy}
          onClick={onConfirm}
        >
          {confirmLabel}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

/** 입력 다이얼로그 — 사유 입력 등. 빈 값(또는 minLength 미만)이면 제출 불가 */
export function PromptDialog({
  title,
  description,
  placeholder,
  submitLabel = '확인',
  danger = false,
  busy = false,
  minLength = 1,
  onSubmit,
  onClose,
  children,
}: {
  title: string;
  description?: ReactNode;
  /** 설명 아래 덧붙일 내용 (함께 처리되는 항목 목록 등) */
  children?: ReactNode;
  placeholder?: string;
  submitLabel?: string;
  danger?: boolean;
  busy?: boolean;
  /** 최소 글자 수 (앞뒤 공백 제외) */
  minLength?: number;
  onSubmit: (value: string) => void;
  onClose: () => void;
}) {
  const [value, setValue] = useState('');
  const ready = value.trim().length >= minLength;
  return (
    <Dialog label={title} title={title} onClose={onClose}>
      {description && <p className="text-sm text-muted-foreground">{description}</p>}
      {children}
      <input
        className={`mt-4 w-full ${inputCls}`}
        placeholder={placeholder}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && ready && !busy) onSubmit(value.trim());
        }}
        autoFocus
      />
      <DialogFooter>
        <Button variant="secondary" className="flex-1" onClick={onClose}>
          취소
        </Button>
        <Button
          variant={danger ? 'danger' : 'primary'}
          className="flex-1"
          disabled={busy || !ready}
          onClick={() => onSubmit(value.trim())}
        >
          {submitLabel}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

// ── 배지 / 칩 ────────────────────────────────────────────────

export type BadgeTone = 'default' | 'blue' | 'green' | 'red' | 'amber' | 'brand';

const BADGE_TONES: Record<BadgeTone, string> = {
  default: 'bg-muted text-muted-foreground',
  blue: 'bg-brand-soft text-brand',
  brand: 'bg-brand-soft text-brand',
  green: 'bg-emerald-50 text-emerald-600',
  red: 'bg-red-50 text-red-600',
  amber: 'bg-amber-50 text-amber-700',
};

export function Badge({
  tone = 'default',
  children,
  className = '',
}: {
  tone?: BadgeTone;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={`inline-flex items-center whitespace-nowrap rounded-md px-2 py-0.5 text-xs font-semibold ${BADGE_TONES[tone]} ${className}`}
    >
      {children}
    </span>
  );
}

/** 필터 칩 (목록 상단 상태 필터 등) — active 는 검정 pill. */
export function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        active
          ? 'rounded-full bg-foreground px-3.5 py-1.5 text-xs font-bold text-background'
          : 'rounded-full bg-muted px-3.5 py-1.5 text-xs font-semibold text-muted-foreground transition hover:bg-border/70'
      }
    >
      {children}
    </button>
  );
}

// ── 버튼 ────────────────────────────────────────────────────

export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'danger' | 'ghost';

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-brand text-white hover:bg-brand-strong',
  secondary: 'bg-muted text-foreground hover:bg-border/70',
  outline: 'border border-border bg-background text-foreground hover:bg-muted',
  danger: 'bg-red-50 text-red-600 hover:bg-red-100',
  ghost: 'text-muted-foreground hover:bg-muted hover:text-foreground',
};

export function Button({
  variant = 'primary',
  size = 'md',
  className = '',
  type = 'button',
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: 'sm' | 'md';
}) {
  const sizeCls = size === 'sm' ? 'px-3 py-1.5 text-xs' : 'px-3.5 py-2 text-sm';
  return (
    <button
      type={type}
      className={`whitespace-nowrap rounded-md font-semibold transition disabled:pointer-events-none disabled:opacity-40 ${sizeCls} ${BUTTON_VARIANTS[variant]} ${className}`}
      {...rest}
    />
  );
}

/** 목록 쪽 넘김 — 한 쪽뿐이면 그리지 않는다 */
export function Pager({
  page,
  totalPages,
  disabled = false,
  onChange,
}: {
  page: number;
  totalPages: number;
  disabled?: boolean;
  onChange(page: number): void;
}) {
  if (totalPages <= 1) return null;
  return (
    <div className="mt-4 flex items-center justify-center gap-3">
      <Button
        variant="outline"
        size="sm"
        disabled={page <= 1 || disabled}
        onClick={() => onChange(page - 1)}
      >
        이전
      </Button>
      <span className="text-sm tabular-nums text-muted-foreground">
        {page} / {totalPages}
      </span>
      <Button
        variant="outline"
        size="sm"
        disabled={page >= totalPages || disabled}
        onClick={() => onChange(page + 1)}
      >
        다음
      </Button>
    </div>
  );
}

// ── 폼 요소 ──────────────────────────────────────────────────

export const inputCls =
  'rounded-md border border-border bg-background px-3 py-2 text-sm transition focus:border-foreground/60 focus:outline-none';

/**
 * 은행 고르기 — 웹 계좌 화면과 같은 목록(PAYOUT_BANKS). 자유 입력이면 "국민"·"KB"·"국민은행"이 섞인다.
 * 목록에 없는 예전 값(구매자가 직접 적은 은행명 등)도 고른 채로 보이게 덧붙인다
 */
export function BankSelect({
  value,
  onChange,
  className = '',
}: {
  value: string;
  onChange(v: string): void;
  className?: string;
}) {
  const listed = (PAYOUT_BANKS as readonly string[]).includes(value);
  const options = [
    ...PAYOUT_BANKS.map((b) => ({ value: b, label: b })),
    ...(value && !listed ? [{ value, label: value }] : []),
  ];
  return (
    <Select
      label="은행"
      value={value}
      options={options}
      onValueChange={onChange}
      placeholder="은행 선택"
      className={className}
    />
  );
}

// ── 테이블 ───────────────────────────────────────────────────
// 데이터 목록은 components/data-table.tsx 의 DataTable(TanStack Table, 컬럼 리사이즈 지원)을 쓰고,
// 아래 프리미티브는 정적/단순 테이블(가이드 등)용.

/** 표준 어드민 테이블 래퍼 — 카드형 라운드 + 헤더 그레이. */
export function TableShell({ children }: { children: ReactNode }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-card shadow-soft">
      <table className="w-full text-sm">{children}</table>
    </div>
  );
}

export function Th({ children, className = '' }: { children?: ReactNode; className?: string }) {
  return (
    <th
      className={`whitespace-nowrap px-4 py-3 text-left text-xs font-semibold text-muted-foreground ${className}`}
    >
      {children}
    </th>
  );
}

export function Td({ children, className = '' }: { children?: ReactNode; className?: string }) {
  return <td className={`px-4 py-3 align-middle ${className}`}>{children}</td>;
}

export function TheadRow({ children }: { children: ReactNode }) {
  return (
    <thead>
      <tr className="border-b border-border bg-muted/60">{children}</tr>
    </thead>
  );
}

/** tbody 행 — hover 하이라이트. onClick 을 주면 클릭 가능한 행. */
export function BodyRow({
  children,
  onClick,
  className = '',
}: {
  children: ReactNode;
  onClick?: () => void;
  className?: string;
}) {
  return (
    <tr
      onClick={onClick}
      className={`border-b border-border/60 transition last:border-0 hover:bg-muted/40 ${onClick ? 'cursor-pointer' : ''} ${className}`}
    >
      {children}
    </tr>
  );
}
